/**
 * ADA Fix: Focus Indicator Contrast
 * 
 * Issue: Keyboard focus indicators (outlines) don't have sufficient contrast
 * against their backgrounds. The white outline (#FFFFFF) on blue background 
 * (#7BA6DE) has a ratio of 2.5:1, which is insufficient.
 * 
 * WCAG 2.2 Requirement: 
 * - 1.4.11 Non-text Contrast (Level AA): Minimum 3:1 contrast ratio for 
 *   UI components and their states (including focus indicators)
 * 
 * Solution: Dynamically detect background colors and apply appropriate
 * focus indicators with sufficient contrast (minimum 3:1 ratio).
 */

(function() {
  'use strict';

  console.log('🎯 ADA Fix: Initializing focus indicator contrast correction...');

  // Configuration
  const CONFIG = {
    minContrastRatio: 3.0, // WCAG 2.2 Level AA for UI components
    focusIndicatorWidth: 3, // pixels
    focusIndicatorOffset: 2, // pixels
    
    // Predefined high-contrast focus styles
    focusStyles: {
      // For light backgrounds
      light: {
        outlineColor: '#000000',
        outlineWidth: '3px',
        outlineOffset: '2px',
        boxShadow: '0 0 0 5px #ffffff, 0 0 0 8px #000000'
      },
      // For dark backgrounds
      dark: {
        outlineColor: '#FFFFFF',
        outlineWidth: '3px',
        outlineOffset: '2px',
        boxShadow: '0 0 0 5px #000000, 0 0 0 8px #ffffff'
      },
      // For medium/colored backgrounds
      highContrast: {
        outlineColor: '#FFFF00', // Yellow for maximum visibility
        outlineWidth: '3px',
        outlineOffset: '2px',
        boxShadow: '0 0 0 5px #000000, 0 0 0 8px #FFFF00'
      }
    }
  };

  /**
   * Calculate relative luminance of a color
   */
  function getRelativeLuminance(hex) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    
    // Apply gamma correction
    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    
    const rLinear = toLinear(r);
    const gLinear = toLinear(g);
    const bLinear = toLinear(b);
    
    // Calculate luminance
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
  }

  /**
   * Calculate contrast ratio between two colors
   */
  function getContrastRatio(color1, color2) {
    const lum1 = getRelativeLuminance(color1);
    const lum2 = getRelativeLuminance(color2);
    
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    
    return (lighter + 0.05) / (darker + 0.05);
  }

  /**
   * Convert RGB color to hex
   */
  function rgbToHex(rgb) {
    if (!rgb || rgb === 'transparent' || rgb === 'rgba(0, 0, 0, 0)') {
      return null;
    }
    
    if (rgb.startsWith('#')) {
      return rgb.toUpperCase();
    }
    
    const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (!match) return null;
    
    const r = parseInt(match[1]).toString(16).padStart(2, '0');
    const g = parseInt(match[2]).toString(16).padStart(2, '0');
    const b = parseInt(match[3]).toString(16).padStart(2, '0');
    
    return `#${r}${g}${b}`.toUpperCase();
  }

  /**
   * Get the background color of an element (considering parent backgrounds)
   */
  function getEffectiveBackgroundColor(element) {
    let current = element;
    let depth = 0;
    const maxDepth = 10;
    
    while (current && depth < maxDepth) {
      const computedStyle = window.getComputedStyle(current);
      const bgColor = computedStyle.backgroundColor;
      const hexColor = rgbToHex(bgColor);
      
      // If we found a non-transparent background, use it
      if (hexColor && hexColor !== '#000000' && bgColor !== 'rgba(0, 0, 0, 0)') {
        return hexColor;
      }
      
      current = current.parentElement;
      depth++;
    }
    
    // Default to white if no background found
    return '#FFFFFF';
  }

  /**
   * Determine the best focus style based on background color
   */
  function getBestFocusStyle(backgroundColor) {
    // Calculate luminance to determine if background is light or dark
    const luminance = getRelativeLuminance(backgroundColor);
    
    // Check contrast with black outline
    const contrastWithBlack = getContrastRatio(backgroundColor, '#000000');
    const contrastWithWhite = getContrastRatio(backgroundColor, '#FFFFFF');
    const contrastWithYellow = getContrastRatio(backgroundColor, '#FFFF00');
    
    // If background is light, use black outline
    if (luminance > 0.5 || contrastWithBlack >= CONFIG.minContrastRatio) {
      return CONFIG.focusStyles.light;
    }
    
    // If background is dark, use white outline
    if (luminance < 0.2 || contrastWithWhite >= CONFIG.minContrastRatio) {
      return CONFIG.focusStyles.dark;
    }
    
    // For medium/colored backgrounds, use high contrast (yellow)
    if (contrastWithYellow >= CONFIG.minContrastRatio) {
      return CONFIG.focusStyles.highContrast;
    }
    
    // Fallback: black outline with strong shadow
    return CONFIG.focusStyles.light;
  }

  /**
   * Apply enhanced focus styling to an element
   */
  function applyEnhancedFocusStyle(element) {
    if (element.hasAttribute('data-ada-focus-enhanced')) {
      return; // Already processed
    }

    const backgroundColor = getEffectiveBackgroundColor(element);
    const focusStyle = getBestFocusStyle(backgroundColor);
    
    // Create unique class for this element
    const focusClass = `ada-focus-enhanced-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    element.classList.add(focusClass);
    
    // Create CSS rule
    const style = document.createElement('style');
    style.textContent = `
      .${focusClass}:focus-visible {
        outline: ${focusStyle.outlineWidth} solid ${focusStyle.outlineColor} !important;
        outline-offset: ${focusStyle.outlineOffset} !important;
        box-shadow: ${focusStyle.boxShadow} !important;
      }
      
      /* Ensure focus is always visible */
      .${focusClass}:focus {
        outline: ${focusStyle.outlineWidth} solid ${focusStyle.outlineColor} !important;
        outline-offset: ${focusStyle.outlineOffset} !important;
      }
    `;
    
    document.head.appendChild(style);
    
    // Mark as processed
    element.setAttribute('data-ada-focus-enhanced', 'true');
    element.setAttribute('data-ada-focus-background', backgroundColor);
    element.setAttribute('data-ada-focus-style', focusStyle.outlineColor);
    
    console.log(`✅ Enhanced focus for element: ${element.tagName.toLowerCase()}.${element.className}`);
    console.log(`   Background: ${backgroundColor}, Outline: ${focusStyle.outlineColor}`);
  }

  /**
   * Process pagination elements
   */
  function processPaginationElements() {
    console.log('🔍 Processing pagination elements...');
    
    // Find all pagination navigation items
    const paginationItems = document.querySelectorAll('.pagination__nav-item > a, .pagination__nav-item > button');
    
    console.log(`   Found ${paginationItems.length} pagination items`);
    
    paginationItems.forEach(function(item) {
      applyEnhancedFocusStyle(item);
    });
  }

  /**
   * Process all focusable elements
   */
  function processAllFocusableElements() {
    console.log('🔍 Processing all focusable elements...');
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'loess-button',
      '[role="button"]',
      '[role="link"]'
    ];
    
    const focusableElements = document.querySelectorAll(focusableSelectors.join(', '));
    let processed = 0;
    
    focusableElements.forEach(function(element) {
      // Skip elements that are not visible
      const computedStyle = window.getComputedStyle(element);
      if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
        return;
      }
      
      // Get background color
      const backgroundColor = getEffectiveBackgroundColor(element);
      
      // Check if the element might have a problematic background
      const isProblematic = 
        backgroundColor === '#7BA6DE' || 
        backgroundColor === '#7BA6DF' ||
        backgroundColor.startsWith('#7') || // Any blue-ish color
        getRelativeLuminance(backgroundColor) > 0.3 && getRelativeLuminance(backgroundColor) < 0.7; // Medium luminance
      
      if (isProblematic) {
        applyEnhancedFocusStyle(element);
        processed++;
      }
    });
    
    console.log(`📊 Processed ${processed} potentially problematic focus elements`);
  }

  /**
   * Setup mutation observer for dynamic content
   */
  function setupDynamicContentObserver() {
    const observer = new MutationObserver(function(mutations) {
      let shouldReprocess = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              // Check if it's a pagination element or focusable element
              if (node.classList && 
                  (node.classList.contains('pagination') || 
                   node.classList.contains('pagination__nav-item') ||
                   node.matches && node.matches('a, button, input, select, textarea'))) {
                shouldReprocess = true;
              }
              
              // Check if it contains pagination or focusable elements
              if (node.querySelector && 
                  (node.querySelector('.pagination') || 
                   node.querySelector('.pagination__nav-item') ||
                   node.querySelector('a, button, input'))) {
                shouldReprocess = true;
              }
            }
          });
        }
      });
      
      if (shouldReprocess) {
        setTimeout(() => {
          console.log('🔄 Reprocessing focus indicators due to DOM changes...');
          processPaginationElements();
        }, 200);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return observer;
  }

  /**
   * Initialize the fix
   */
  function initialize() {
    console.log('🚀 Initializing ADA Focus Contrast Fix...');
    
    // Process pagination elements (high priority)
    processPaginationElements();
    
    // Process other focusable elements with potentially problematic backgrounds
    processAllFocusableElements();
    
    // Setup observer for dynamic content
    setupDynamicContentObserver();
    
    console.log('✅ ADA Focus Contrast Fix initialized');
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Also run after a delay for dynamic content
  setTimeout(initialize, 1000);

  // Re-run when pagination is loaded
  document.addEventListener('loess-pagination:loaded', function() {
    console.log('🔄 Pagination loaded, reprocessing focus indicators...');
    setTimeout(processPaginationElements, 100);
  });

  // Expose functions for testing
  window.adaFocusContrastFix = {
    processPaginationElements,
    processAllFocusableElements,
    applyEnhancedFocusStyle,
    getContrastRatio,
    getEffectiveBackgroundColor
  };

  console.log('🎯 ADA Focus Contrast Fix loaded. Functions available in window.adaFocusContrastFix');

})();
